<script setup lang="ts">
import { FetchError } from 'ofetch'
import { VForm } from 'vuetify/components/VForm'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import type { App } from '@/types/generated'
import { Modules } from '@/types/generated'

type CatalogItem = App.Http.Controllers.RealtimeAvatar.RealtimeAvatarCatalogItemData
type Gender = Modules.RealtimeAvatar.Enums.RealtimeAvatarGenderEnum
type Provider = Modules.AIConnector.Enums.ProviderEnum
type FieldErrors = Partial<Record<string, string[]>>

interface OrganizationOption {
  id: string
  title: string
}

definePage({
  meta: {
    action: 'manage',
    subject: 'all',
  },
})

const { t } = useI18n()

// VSelect treats a null model as "nothing selected", so shared visibility needs its own value.
const ALL_ORGANIZATIONS = 'all'

const AVATAR_FIELDS = ['name', 'provider', 'externalId', 'gender', 'organizationId']

const PROVIDER_TITLES: Partial<Record<Provider, string>> = {
  [Modules.AIConnector.Enums.ProviderEnum.ANAM]: 'Anam',
}

const { data: avatarsData, execute: fetchAvatars, isFetching } = await useApi<CatalogItem[]>('/realtime-avatars')
const { data: organizationsData } = await useApi<OrganizationOption[]>('/organizations/all')

const organizations = computed<OrganizationOption[]>(() => organizationsData.value ?? [])

const organizationTitle = (organizationId: string | null): string => {
  if (organizationId === null)
    return t('realtimeAvatars.visibility.all')

  return organizations.value.find(organization => organization.id === organizationId)?.title ?? organizationId
}

const providerTitle = (provider: Provider): string => PROVIDER_TITLES[provider] ?? provider

const rows = computed(() => (avatarsData.value ?? []).map(avatar => ({
  ...avatar,
  providerTitle: providerTitle(avatar.provider),
  modelsTitle: Object.values(avatar.availableModels).join(', '),
  visibilityTitle: organizationTitle(avatar.organizationId),
})))

const searchQuery = ref('')

const headers = [
  { title: t('realtimeAvatars.headers.poster'), key: 'posterUrl', sortable: false },
  { title: t('realtimeAvatars.headers.name'), key: 'name' },
  { title: t('realtimeAvatars.headers.provider'), key: 'providerTitle' },
  { title: t('realtimeAvatars.headers.externalId'), key: 'externalId' },
  { title: t('realtimeAvatars.headers.gender'), key: 'gender' },
  { title: t('realtimeAvatars.headers.visibility'), key: 'visibilityTitle' },
  { title: t('realtimeAvatars.headers.profiles'), key: 'profilesCount' },
  { title: t('realtimeAvatars.headers.models'), key: 'modelsTitle', sortable: false },
  { title: '', key: 'actions', sortable: false },
]

const providerOptions = Object.keys(PROVIDER_TITLES)
  .map(provider => ({ value: provider, title: providerTitle(provider as Provider) }))

const genderOptions = Object.values(Modules.RealtimeAvatar.Enums.RealtimeAvatarGenderEnum)
  .map(gender => ({ value: gender, title: t(`realtimeAvatars.gender.${gender}`) }))

const visibilityOptions = computed(() => [
  { value: ALL_ORGANIZATIONS, title: t('realtimeAvatars.visibility.all') },
  ...organizations.value.map(organization => ({ value: organization.id, title: organization.title })),
])

const toOrganizationId = (visibility: string): string | null => visibility === ALL_ORGANIZATIONS ? null : visibility

const toVisibility = (organizationId: string | null): string => organizationId ?? ALL_ORGANIZATIONS

const requestFailedMessage = () => t('realtimeAvatars.errors.requestFailed')

const toFieldErrors = (error: unknown): FieldErrors => {
  const errors: unknown = error instanceof FetchError && error.statusCode === 422 ? error.data?.errors : undefined

  if (!errors || typeof errors !== 'object')
    return { '': [requestFailedMessage()] }

  const fieldErrors: FieldErrors = {}

  for (const [key, messages] of Object.entries(errors)) {
    const field = key.split('.')[0]

    fieldErrors[field] = [...(fieldErrors[field] ?? []), ...[messages].flat().map(String)]
  }

  return fieldErrors
}

const otherErrors = (errors: FieldErrors): string[] => Object.entries(errors)
  .filter(([field]) => !AVATAR_FIELDS.includes(field))
  .flatMap(([, messages]) => messages ?? [])

const pageError = ref('')
const isPageErrorVisible = ref(false)

const showPageError = (message: string) => {
  pageError.value = message
  isPageErrorVisible.value = true
}

const reducedMotion = usePreferredReducedMotion()
const isPreviewVisible = ref(false)
const previewTarget = ref<CatalogItem | null>(null)
const isPreviewFailed = ref(false)

const openPreview = (avatar: CatalogItem) => {
  previewTarget.value = avatar
  isPreviewFailed.value = false
  isPreviewVisible.value = true
}

const isAvatarDialogVisible = ref(false)
const isDetachConfirmVisible = ref(false)
const avatarTarget = ref<CatalogItem | null>(null)
const avatarForm = ref<VForm>()
const avatarErrors = ref<FieldErrors>({})
const isSaving = ref(false)
const detachProfilesCount = ref(0)

const emptyAvatarData = () => ({
  name: '',
  provider: Modules.AIConnector.Enums.ProviderEnum.ANAM,
  externalId: '',
  gender: Modules.RealtimeAvatar.Enums.RealtimeAvatarGenderEnum.Male as Gender,
  visibility: ALL_ORGANIZATIONS,
})

const avatarData = ref(emptyAvatarData())

const openCreateDialog = () => {
  avatarTarget.value = null
  avatarData.value = emptyAvatarData()
  avatarErrors.value = {}
  isAvatarDialogVisible.value = true
}

const openEditDialog = (avatar: CatalogItem) => {
  avatarTarget.value = avatar
  avatarData.value = {
    name: avatar.name,
    provider: avatar.provider,
    externalId: avatar.externalId,
    gender: avatar.gender,
    visibility: toVisibility(avatar.organizationId),
  }
  avatarErrors.value = {}
  isAvatarDialogVisible.value = true
}

const createAvatar = async () => {
  const name = avatarData.value.name.trim()

  const body: App.Http.Controllers.RealtimeAvatar.StoreRealtimeAvatarData = {
    provider: avatarData.value.provider,
    externalId: avatarData.value.externalId,
    gender: avatarData.value.gender,
    name: name === '' ? null : name,
    organizationId: toOrganizationId(avatarData.value.visibility),
  }

  isSaving.value = true
  avatarErrors.value = {}
  try {
    await $api('/realtime-avatars', { method: 'POST', body })
    isAvatarDialogVisible.value = false
    await fetchAvatars()
  }
  catch (error) {
    avatarErrors.value = toFieldErrors(error)
  }
  finally {
    isSaving.value = false
  }
}

const saveEdit = async () => {
  const avatar = avatarTarget.value
  if (avatar === null)
    return

  const body: App.Http.Controllers.RealtimeAvatar.UpdateRealtimeAvatarData = {
    name: avatarData.value.name,
    provider: avatarData.value.provider,
    externalId: avatarData.value.externalId,
    gender: avatarData.value.gender,
    organizationId: toOrganizationId(avatarData.value.visibility),
  }

  isSaving.value = true
  avatarErrors.value = {}
  try {
    await $api(`/realtime-avatars/${avatar.id}`, { method: 'PUT', body })
    isAvatarDialogVisible.value = false
    await fetchAvatars()
  }
  catch (error) {
    avatarErrors.value = toFieldErrors(error)
  }
  finally {
    isSaving.value = false
  }
}

const submitEdit = async (avatar: CatalogItem) => {
  const newOrganizationId = toOrganizationId(avatarData.value.visibility)
  const narrowsVisibility = newOrganizationId !== null && newOrganizationId !== avatar.organizationId

  if (!narrowsVisibility) {
    await saveEdit()

    return
  }

  isSaving.value = true
  avatarErrors.value = {}
  try {
    const preview = await $api<{ profiles: number }>(`/realtime-avatars/${avatar.id}/detach-preview`, {
      query: { organizationId: newOrganizationId },
    })

    detachProfilesCount.value = preview.profiles
  }
  catch (error) {
    avatarErrors.value = toFieldErrors(error)

    return
  }
  finally {
    isSaving.value = false
  }

  if (detachProfilesCount.value > 0)
    isDetachConfirmVisible.value = true
  else
    await saveEdit()
}

const submitAvatar = async () => {
  if (isSaving.value)
    return

  const { valid } = await avatarForm.value!.validate()
  if (!valid)
    return

  if (avatarTarget.value === null)
    await createAvatar()
  else
    await submitEdit(avatarTarget.value)
}

const isDeleteConfirmVisible = ref(false)
const deleteTarget = ref<CatalogItem | null>(null)

const deleteQuestion = computed(() => {
  const avatar = deleteTarget.value
  if (avatar === null)
    return ''

  return avatar.profilesCount > 0
    ? t('realtimeAvatars.confirmDeleteWithProfiles', { name: avatar.name, count: avatar.profilesCount })
    : t('realtimeAvatars.confirmDelete', { name: avatar.name })
})

const openDeleteConfirm = (avatar: CatalogItem) => {
  deleteTarget.value = avatar
  isDeleteConfirmVisible.value = true
}

const deleteAvatar = async () => {
  const avatar = deleteTarget.value
  if (avatar === null)
    return

  try {
    await $api(`/realtime-avatars/${avatar.id}`, { method: 'DELETE' })
  }
  catch {
    showPageError(requestFailedMessage())
  }
  await fetchAvatars()
}
</script>

<template>
  <div>
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <VTextField
            v-model="searchQuery"
            clearable
            :placeholder="t('search')"
            style=" min-inline-size: 200px;"
          />
        </div>
        <VSpacer />
        <VBtn
          prepend-icon="tabler-plus"
          @click="openCreateDialog"
        >
          {{ t('realtimeAvatars.create') }}
        </VBtn>
      </VCardText>

      <VDataTable
        :headers="headers"
        :items="rows"
        :search="searchQuery ?? ''"
        :loading="isFetching"
        :loading-text="t('loading')"
        :no-data-text="t('realtimeAvatars.empty')"
        :items-per-page="-1"
        item-value="id"
        class="text-no-wrap"
      >
        <template #item.posterUrl="{ item }">
          <VImg
            :src="item.posterUrl"
            :alt="item.name"
            width="72"
            height="48"
            cover
            class="rounded my-2"
          />
        </template>
        <template #item.gender="{ item }">
          {{ t(`realtimeAvatars.gender.${item.gender}`) }}
        </template>
        <template #item.modelsTitle="{ item }">
          {{ item.modelsTitle || '—' }}
        </template>
        <template #item.actions="{ item }">
          <div class="d-flex justify-end">
            <IconBtn
              v-if="item.previewUrl"
              :title="t('realtimeAvatars.preview')"
              @click="openPreview(item)"
            >
              <VIcon icon="tabler-player-play" />
            </IconBtn>
            <IconBtn
              :title="t('realtimeAvatars.edit')"
              @click="openEditDialog(item)"
            >
              <VIcon icon="tabler-edit" />
            </IconBtn>
            <IconBtn
              :title="t('realtimeAvatars.delete')"
              @click="openDeleteConfirm(item)"
            >
              <VIcon icon="tabler-trash" />
            </IconBtn>
          </div>
        </template>
        <template #bottom />
      </VDataTable>
    </VCard>

    <VDialog
      v-model="isAvatarDialogVisible"
      :width="$vuetify.display.smAndDown ? 'auto' : 680"
      :persistent="isSaving"
    >
      <DialogCloseBtn
        :disabled="isSaving"
        @click="isAvatarDialogVisible = false"
      />

      <VCard class="pa-5 pa-sm-8">
        <VCardItem class="text-center">
          <VCardTitle class="text-h5 mb-3">
            {{ avatarTarget ? t('realtimeAvatars.edit') : t('realtimeAvatars.create') }}
          </VCardTitle>
        </VCardItem>

        <VCardText>
          <VForm
            ref="avatarForm"
            :disabled="isSaving"
            @submit.prevent="submitAvatar"
          >
            <VRow>
              <VCol
                v-if="otherErrors(avatarErrors).length"
                cols="12"
              >
                <VAlert
                  type="error"
                  variant="tonal"
                >
                  <div
                    v-for="(message, index) in otherErrors(avatarErrors)"
                    :key="index"
                  >
                    {{ message }}
                  </div>
                </VAlert>
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <AppSelect
                  v-model="avatarData.provider"
                  :items="providerOptions"
                  :label="t('realtimeAvatars.headers.provider')"
                  :rules="[requiredValidator]"
                  :error-messages="avatarErrors.provider"
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <AppTextField
                  v-model="avatarData.externalId"
                  :label="t('realtimeAvatars.headers.externalId')"
                  :rules="[requiredValidator]"
                  :error-messages="avatarErrors.externalId"
                  :hint="avatarTarget ? t('realtimeAvatars.externalIdHint') : undefined"
                  :persistent-hint="Boolean(avatarTarget)"
                  maxlength="255"
                />
              </VCol>
              <VCol cols="12">
                <AppTextField
                  v-model="avatarData.name"
                  :label="t('realtimeAvatars.headers.name')"
                  :rules="avatarTarget ? [requiredValidator] : []"
                  :error-messages="avatarErrors.name"
                  :hint="avatarTarget ? undefined : t('realtimeAvatars.nameHint')"
                  :persistent-hint="!avatarTarget"
                  maxlength="255"
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <AppSelect
                  v-model="avatarData.gender"
                  :items="genderOptions"
                  :label="t('realtimeAvatars.headers.gender')"
                  :error-messages="avatarErrors.gender"
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <AppSelect
                  v-model="avatarData.visibility"
                  :items="visibilityOptions"
                  :label="t('realtimeAvatars.headers.visibility')"
                  :error-messages="avatarErrors.organizationId"
                />
              </VCol>
              <VCol
                cols="12"
                class="d-flex flex-wrap justify-center gap-4"
              >
                <VBtn
                  type="submit"
                  :loading="isSaving"
                  :disabled="isSaving"
                >
                  {{ t('save') }}
                </VBtn>
                <VBtn
                  color="secondary"
                  variant="tonal"
                  :disabled="isSaving"
                  @click="isAvatarDialogVisible = false"
                >
                  {{ t('cancel') }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VDialog>

    <VDialog
      v-model="isPreviewVisible"
      :width="$vuetify.display.smAndDown ? 'auto' : 680"
      @after-leave="previewTarget = null"
    >
      <DialogCloseBtn @click="isPreviewVisible = false" />

      <VCard
        v-if="previewTarget?.previewUrl"
        class="pa-5 pa-sm-8"
      >
        <VCardItem class="text-center">
          <VCardTitle class="text-h5 mb-3">
            {{ previewTarget.name }}
          </VCardTitle>
        </VCardItem>

        <VCardText>
          <VAlert
            v-if="isPreviewFailed"
            type="error"
            variant="tonal"
          >
            {{ t('realtimeAvatars.errors.previewUnavailable') }}
          </VAlert>
          <video
            v-else
            :key="previewTarget.id"
            class="realtime-avatars__preview"
            :src="previewTarget.previewUrl"
            :poster="previewTarget.posterUrl"
            :autoplay="reducedMotion !== 'reduce'"
            muted
            loop
            playsinline
            controls
            disablepictureinpicture
            @error="isPreviewFailed = true"
          />
        </VCardText>
      </VCard>
    </VDialog>

    <ConfirmDialog
      v-model:isDialogVisible="isDetachConfirmVisible"
      :confirmation-question="t('realtimeAvatars.confirmDetach', { count: detachProfilesCount })"
      confirm-title=""
      confirm-msg=""
      cancel-title=""
      cancel-msg=""
      @confirm="saveEdit"
    />

    <ConfirmDialog
      v-model:isDialogVisible="isDeleteConfirmVisible"
      :confirmation-question="deleteQuestion"
      confirm-title=""
      confirm-msg=""
      cancel-title=""
      cancel-msg=""
      @confirm="deleteAvatar"
    />

    <VSnackbar
      v-model="isPageErrorVisible"
      color="error"
    >
      {{ pageError }}
    </VSnackbar>
  </div>
</template>

<style lang="scss" scoped>
.realtime-avatars__preview {
  display: block;
  border-radius: 8px;
  aspect-ratio: 3 / 2;
  background-color: rgb(0, 0, 0);
  inline-size: 100%;
}
</style>
